import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Search, User, Menu, X, ChevronDown, LogOut, Settings } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface Promotion { id: number; text: string; }

export default function Navbar() {
  const { itemCount, toggleCart } = useCart();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get('/promotions')
      .then(r => setPromotions(r.data))
      .catch(() => {
        setPromotions([
          { id: 1, text: 'FREE DELIVERIES ON ORDERS ABOVE RS. 10,000' },
          { id: 2, text: 'HAPPY & HEALTHY COMPANIONS FOR ADOPTION' },
          { id: 3, text: 'CERTIFIED VETERINARY HEALTH INSPECTIONS' }
        ]);
      });
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setUserMenuOpen(false);
    setMobileOpen(false);
  };

  const tickerItems = promotions.map(p => p.text);

  const LogoCircle = ({ small = false }: { small?: boolean }) => (
    <div className={`${small ? 'w-11 h-11' : 'w-12 h-12 md:w-14 md:h-14'} rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 shadow-md transition-all duration-300 group-hover:rotate-12 border border-gray-200 bg-white`}>
      <img src="/logo.jpeg" alt="Royal Pet Store Logo" className="w-full h-full object-cover" />
    </div>
  );

  return (
    <>
      {/* ── Promotions Ticker ── */}
      {tickerItems.length > 0 && <div className="bg-gradient-to-r from-primary via-[#2E1E17] to-primary overflow-hidden py-2 relative border-b border-accent/10">
        <style>{`
          @keyframes ticker-scroll {
            0%   { transform: translateX(100vw); }
            100% { transform: translateX(-100%); }
          }
          .ticker-track { animation: ticker-scroll 35s linear infinite; }
          .ticker-track:hover { animation-play-state: paused; }
        `}</style>
        <div className="flex whitespace-nowrap ticker-track">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => (
            <span key={i} className="inline-flex items-center gap-3 px-6 text-white text-[10px] font-bold tracking-[0.2em] uppercase">
              <span className="text-accent font-serif">✦</span>
              {item}
            </span>
          ))}
        </div>
      </div>}

      {/* ── Announcement Bar ── */}
      <div className="bg-[#FCFAF7] text-primary text-[10px] text-center py-2 px-4 font-bold tracking-widest border-b border-gray-100 flex items-center justify-center gap-2">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
        VERIFIED BREEDERS &nbsp;|&nbsp; SAFE CLIMATE TRANSIT &nbsp;|&nbsp; CALL / WHATSAPP: 0329 5025172
      </div>

      <nav className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'shadow-premium glass-premium py-1' : 'bg-white/95 border-b border-gray-100 py-2'}`}>

        {/* TOP ROW: Logo + Nav + Icons */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 md:h-[68px]">

            {/* Left: Hamburger (mobile) + Logo */}
            <div className="flex items-center gap-3 flex-none">
              <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 -ml-2 text-gray-700 hover:text-accent transition-colors" aria-label="Open menu">
                <Menu size={22} />
              </button>
              <Link to="/" className="flex items-center gap-2 flex-shrink-0 group">
                <LogoCircle />
                <div className="leading-tight">
                  <div className="flex items-center">
                    <span className="text-[19px] md:text-[22px] font-serif font-extrabold tracking-tight text-primary">Royal Pet Store</span>
                    <span className="text-[9px] md:text-[10px] font-bold bg-accent text-white px-2 py-0.5 tracking-wider ml-2 rounded-full uppercase">Pro</span>
                  </div>
                  <span className="block text-[7px] md:text-[8px] font-bold tracking-[0.3em] text-accent/80 uppercase -mt-0.5">Premium Pet Boutique</span>
                </div>
              </Link>
            </div>

            {/* Center: Desktop nav */}
            <div className="hidden lg:flex flex-1 items-center justify-center gap-5 xl:gap-8 mx-4">
              <Link to="/new-in" className="relative text-gray-800 text-[11px] font-bold tracking-widest uppercase hover:text-accent transition-colors py-1.5 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:w-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300 hover:after:w-full hover:after:left-0">New Arrivals</Link>
              <Link to="/category/pets" className="relative text-gray-800 text-[11px] font-bold tracking-widest uppercase hover:text-accent transition-colors py-1.5 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:w-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300 hover:after:w-full hover:after:left-0">Pets</Link>
              <Link to="/category/pet-foods" className="relative text-gray-800 text-[11px] font-bold tracking-widest uppercase hover:text-accent transition-colors py-1.5 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:w-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300 hover:after:w-full hover:after:left-0">Pet Foods</Link>
              <Link to="/category/pet-accessories" className="relative text-gray-800 text-[11px] font-bold tracking-widest uppercase hover:text-accent transition-colors py-1.5 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:w-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300 hover:after:w-full hover:after:left-0">Pet Accessories</Link>
              
              {/* Info Dropdown */}
              <div className="relative group">
                <span className="text-gray-800 text-[11px] font-bold tracking-widest uppercase hover:text-accent transition-colors flex items-center gap-0.5 cursor-pointer py-1.5">
                  Info <ChevronDown size={11} className="mt-0.5" />
                </span>
                <div className="absolute top-full left-0 bg-white/95 backdrop-blur-md shadow-premium border border-gray-100 min-w-[200px] py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 translate-y-2 group-hover:translate-y-0 z-50 rounded-xl overflow-hidden">
                  <Link to="/about" className="block px-4 py-2.5 text-[11px] text-gray-700 hover:bg-accent/10 hover:text-primary uppercase tracking-wider font-bold transition-all">About Us</Link>
                  <Link to="/adoption-guide" className="block px-4 py-2.5 text-[11px] text-gray-700 hover:bg-accent/10 hover:text-primary uppercase tracking-wider font-bold transition-all">Adoption Guide</Link>
                  <Link to="/pet-care" className="block px-4 py-2.5 text-[11px] text-gray-700 hover:bg-accent/10 hover:text-primary uppercase tracking-wider font-bold transition-all">Pet Care Tips</Link>
                  <Link to="/faqs" className="block px-4 py-2.5 text-[11px] text-gray-700 hover:bg-accent/10 hover:text-primary uppercase tracking-wider font-bold transition-all">FAQs</Link>
                  <Link to="/refund-policy" className="block px-4 py-2.5 text-[11px] text-gray-700 hover:bg-accent/10 hover:text-primary uppercase tracking-wider font-bold transition-all">Store Policies</Link>
                  <Link to="/shipping-policy" className="block px-4 py-2.5 text-[11px] text-gray-700 hover:bg-accent/10 hover:text-primary uppercase tracking-wider font-bold transition-all">Transit Info</Link>
                </div>
              </div>

              <Link to="/contact" className="relative text-gray-800 text-[11px] font-bold tracking-widest uppercase hover:text-accent transition-colors py-1.5 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:w-0 after:h-0.5 after:bg-accent after:transition-all after:duration-300 hover:after:w-full hover:after:left-0">Contact</Link>
            </div>

            {/* Right: Icons */}
            <div className="flex items-center gap-1 sm:gap-2 flex-none">
              <button onClick={() => setSearchOpen(v => !v)} className="p-2.5 text-gray-700 hover:text-accent hover:scale-105 transition-all" aria-label="Search">
                <Search size={19} />
              </button>
              <div className="relative" ref={userMenuRef}>
                <button onClick={() => setUserMenuOpen(v => !v)} className="p-2.5 text-gray-700 hover:text-accent hover:scale-105 transition-all" aria-label="Account">
                  <User size={19} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 bg-white/95 backdrop-blur-md shadow-premium border border-gray-100 w-56 py-1 z-50 rounded-2xl overflow-hidden">
                    {isAuthenticated ? (
                      <>
                        <div className="px-5 py-3.5 border-b border-gray-50 bg-gray-50/50">
                          <p className="text-xs font-bold text-gray-900 truncate">{user?.name}</p>
                          <p className="text-[10px] font-medium text-gray-500 truncate mt-0.5">{user?.email}</p>
                        </div>
                        <Link to="/account" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-xs font-semibold text-gray-700 hover:bg-accent/10 hover:text-primary transition-all">
                          <User size={15} className="text-gray-400" /> My Account
                        </Link>
                        {isAdmin && (
                          <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-3 text-xs font-semibold text-gray-700 hover:bg-accent/10 hover:text-primary transition-all">
                            <Settings size={15} className="text-gray-400" /> Admin Panel
                          </Link>
                        )}
                        <div className="border-t border-gray-100 mt-1">
                          <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-3 text-xs font-semibold text-red-600 hover:bg-red-50 w-full text-left transition-all">
                            <LogOut size={15} className="text-red-400" /> Sign Out
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <Link to="/login" onClick={() => setUserMenuOpen(false)} className="block px-5 py-3 text-xs font-bold text-gray-800 hover:bg-accent/10 hover:text-primary transition-all">Sign In</Link>
                        <Link to="/register" onClick={() => setUserMenuOpen(false)} className="block px-5 py-3 text-xs font-semibold text-gray-600 hover:bg-accent/10 hover:text-primary transition-all">Create Account</Link>
                      </>
                    )}
                  </div>
                )}
              </div>
              <button onClick={toggleCart} className="relative p-2.5 text-gray-700 hover:text-accent hover:scale-105 transition-all" aria-label="Cart">
                <ShoppingBag size={19} />
                {itemCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-accent text-white text-[8px] font-bold rounded-full flex items-center justify-center shadow-sm">
                    {itemCount > 9 ? '9+' : itemCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Search overlay */}
        {searchOpen && (
          <div className="absolute top-full left-0 right-0 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-premium z-50 transition-all duration-300">
            <div className="max-w-2xl mx-auto px-4 py-5">
              <form onSubmit={handleSearch} className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/15 transition-all">
                <Search size={18} className="text-gray-400 flex-shrink-0" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search for pure breeds, healthy food, accessories..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="flex-1 text-sm bg-transparent outline-none text-gray-900 placeholder-gray-400"
                />
                <button type="button" onClick={() => setSearchOpen(false)} className="text-gray-400 hover:text-gray-700 transition-colors">
                  <X size={18} />
                </button>
              </form>
            </div>
          </div>
        )}
      </nav>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-primary/40 backdrop-blur-sm transition-opacity" onClick={() => setMobileOpen(false)} />
          <div className="absolute top-0 left-0 h-full w-[min(85vw,320px)] bg-white shadow-premium flex flex-col animate-slide-in-left">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <Link to="/" className="flex items-center gap-2 group" onClick={() => setMobileOpen(false)}>
                <LogoCircle small />
                <span className="text-[17px] font-serif font-extrabold tracking-tight text-primary">Royal Pet Store</span>
              </Link>
              <button onClick={() => setMobileOpen(false)} className="p-1.5 text-gray-600 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-5 px-4 space-y-1">
              <Link to="/new-in" onClick={() => setMobileOpen(false)} className="flex items-center px-4 py-3 text-gray-800 text-xs font-bold uppercase tracking-wider hover:bg-accent/10 hover:text-primary rounded-xl transition-all">New Arrivals</Link>
              <Link to="/category/pets" onClick={() => setMobileOpen(false)} className="flex items-center px-4 py-3 text-gray-800 text-xs font-bold uppercase tracking-wider hover:bg-accent/10 hover:text-primary rounded-xl transition-all">Pets</Link>
              <Link to="/category/pet-foods" onClick={() => setMobileOpen(false)} className="flex items-center px-4 py-3 text-gray-800 text-xs font-bold uppercase tracking-wider hover:bg-accent/10 hover:text-primary rounded-xl transition-all">Pet Foods</Link>
              <Link to="/category/pet-accessories" onClick={() => setMobileOpen(false)} className="flex items-center px-4 py-3 text-gray-800 text-xs font-bold uppercase tracking-wider hover:bg-accent/10 hover:text-primary rounded-xl transition-all">Pet Accessories</Link>
              
              <div className="border-t border-gray-100 mt-4 pt-4">
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest px-4 mb-2">Store Information</p>
                <Link to="/about" onClick={() => setMobileOpen(false)} className="flex items-center px-4 py-2.5 text-gray-700 text-sm font-semibold hover:bg-accent/5 rounded-xl transition-all">About Us</Link>
                <Link to="/adoption-guide" onClick={() => setMobileOpen(false)} className="flex items-center px-4 py-2.5 text-gray-700 text-sm font-semibold hover:bg-accent/5 rounded-xl transition-all">Adoption Guide</Link>
                <Link to="/pet-care" onClick={() => setMobileOpen(false)} className="flex items-center px-4 py-2.5 text-gray-700 text-sm font-semibold hover:bg-accent/5 rounded-xl transition-all">Pet Care Tips</Link>
                <Link to="/faqs" onClick={() => setMobileOpen(false)} className="flex items-center px-4 py-2.5 text-gray-700 text-sm font-semibold hover:bg-accent/5 rounded-xl transition-all">FAQs</Link>
                <Link to="/refund-policy" onClick={() => setMobileOpen(false)} className="flex items-center px-4 py-2.5 text-gray-700 text-sm font-semibold hover:bg-accent/5 rounded-xl transition-all">Store Policies</Link>
                <Link to="/shipping-policy" onClick={() => setMobileOpen(false)} className="flex items-center px-4 py-2.5 text-gray-700 text-sm font-semibold hover:bg-accent/5 rounded-xl transition-all">Transit Info</Link>
                <Link to="/contact" onClick={() => setMobileOpen(false)} className="flex items-center px-4 py-2.5 text-gray-700 text-sm font-semibold hover:bg-accent/5 rounded-xl transition-all">Contact Us</Link>
              </div>
              <div className="border-t border-gray-100 mt-4 pt-4">
                {isAuthenticated ? (
                  <>
                    <Link to="/account" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-gray-850 text-sm font-bold hover:bg-accent/5 rounded-xl transition-all">
                      <User size={16} className="text-gray-400" /> My Account
                    </Link>
                    {isAdmin && (
                      <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-gray-850 text-sm font-bold hover:bg-accent/5 rounded-xl transition-all">
                        <Settings size={16} className="text-gray-400" /> Admin Panel
                      </Link>
                    )}
                    <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-3 text-red-600 text-sm font-bold hover:bg-red-50 rounded-xl w-full text-left transition-all">
                      <LogOut size={16} className="text-red-400" /> Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-gray-850 text-sm font-bold hover:bg-accent/5 rounded-xl transition-all">
                      <User size={16} className="text-gray-400" /> Sign In
                    </Link>
                    <Link to="/register" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-4 py-3 text-gray-800 text-sm font-bold hover:bg-accent/5 rounded-xl transition-all">
                      Create Account
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
